import {
  SelectTabData,
  SelectTabEvent,
  Tab,
  TabList,
} from "@fluentui/react-components";
import { useCallback, useMemo, useState } from "react";
import { useExecutiveAccess } from "apps/project-iq/hooks/useExecutiveAccess";
import { usePortfolios } from "apps/project-iq/hooks/usePortfolios";
import { usePortfolioViewerAccess } from "apps/project-iq/hooks/usePortfolioViewerAccess";
import { usePractices } from "apps/project-iq/hooks/usePractices";
import { usePracticeViewerAccess } from "apps/project-iq/hooks/usePracticeViewerAccess";
import { IExecutiveAccess } from "apps/project-iq/models/IExecutiveAccess";
import { IPortfolioViewerAccess } from "apps/project-iq/models/IPortfolioViewerAccess";
import { IPracticeViewerAccess } from "apps/project-iq/models/IPracticeViewerAccess";
import { useAdminColumnStyles } from "components/adminList/AdminTable.styles";
import { ITableColumn } from "models/ITableColumn";
import { AccessListSection } from "./AccessListSection";
import { ExecutiveAccessDialog } from "./ExecutiveAccessDialog";
import { PortfolioViewerAccessDialog } from "./PortfolioViewerAccessDialog";
import { usePortfoliosPracticesAccessStyles } from "./PortfoliosPracticesAccess.styles";
import { PortfoliosPracticesAccessTab } from "./PortfoliosPracticesAccess.types";
import { PracticeViewerAccessDialog } from "./PracticeViewerAccessDialog";

export const PortfoliosPracticesAccess = (): JSX.Element => {
  const styles = usePortfoliosPracticesAccessStyles();
  const colStyles = useAdminColumnStyles();
  const [activeTab, setActiveTab] =
    useState<PortfoliosPracticesAccessTab>("portfolioViewer");

  const { portfolios } = usePortfolios();
  const { practices } = usePractices();

  const portfolioLabelById = useMemo(
    () =>
      new Map(portfolios.map((portfolio) => [portfolio.id, portfolio.label])),
    [portfolios],
  );
  const practiceLabelById = useMemo(
    () => new Map(practices.map((practice) => [practice.id, practice.label])),
    [practices],
  );

  const {
    portfolioViewerAccessList,
    isLoading: isPortfolioViewerLoading,
    isError: isPortfolioViewerError,
    createMutation: createPortfolioViewerMutation,
    updateMutation: updatePortfolioViewerMutation,
    deleteMutation: deletePortfolioViewerMutation,
  } = usePortfolioViewerAccess();

  const [portfolioViewerDialogState, setPortfolioViewerDialogState] = useState<{
    isOpen: boolean;
    editingItem: IPortfolioViewerAccess | null;
  }>({
    isOpen: false,
    editingItem: null,
  });
  const [portfolioViewerDeleteTarget, setPortfolioViewerDeleteTarget] =
    useState<IPortfolioViewerAccess | null>(null);

  const handleOpenPortfolioViewerDialog = useCallback(
    (item: IPortfolioViewerAccess | null = null) => {
      setPortfolioViewerDialogState({ isOpen: true, editingItem: item });
    },
    [],
  );
  const handleClosePortfolioViewerDialog = useCallback(() => {
    setPortfolioViewerDialogState({ isOpen: false, editingItem: null });
  }, []);

  const handleSavePortfolioViewer = useCallback(
    async (values: {
      user: { userId: string; displayName?: string };
      portfolioIds: string[];
    }) => {
      if (portfolioViewerDialogState.editingItem) {
        await updatePortfolioViewerMutation.mutateAsync({
          ...portfolioViewerDialogState.editingItem,
          ...values,
        });
      } else {
        await createPortfolioViewerMutation.mutateAsync(values);
      }
      handleClosePortfolioViewerDialog();
    },
    [
      portfolioViewerDialogState.editingItem,
      updatePortfolioViewerMutation,
      createPortfolioViewerMutation,
      handleClosePortfolioViewerDialog,
    ],
  );

  const handleConfirmDeletePortfolioViewer = useCallback(async () => {
    if (!portfolioViewerDeleteTarget) return;
    await deletePortfolioViewerMutation.mutateAsync(
      portfolioViewerDeleteTarget.id,
    );
    setPortfolioViewerDeleteTarget(null);
  }, [portfolioViewerDeleteTarget, deletePortfolioViewerMutation]);

  const portfolioViewerColumns = useMemo<
    ITableColumn<IPortfolioViewerAccess>[]
  >(
    () => [
      {
        columnId: "name",
        displayValue: "Name",
        className: colStyles.normal,
        renderHeaderCell: () => "Name",
        compare: (a, b) =>
          (a.user.displayName ?? "").localeCompare(b.user.displayName ?? ""),
        renderCell: (item) => item.user.displayName || item.user.userId,
      },
      {
        columnId: "portfolios",
        displayValue: "Portfolios",
        className: colStyles.wide,
        renderHeaderCell: () => "Portfolios",
        compare: (a, b) =>
          a.portfolioIds
            .map((id) => portfolioLabelById.get(id) ?? "")
            .join(", ")
            .localeCompare(
              b.portfolioIds
                .map((id) => portfolioLabelById.get(id) ?? "")
                .join(", "),
            ),
        renderCell: (item) =>
          item.portfolioIds
            .map((id) => portfolioLabelById.get(id))
            .filter((label): label is string => Boolean(label))
            .join(", ") || "—",
      },
    ],
    [colStyles, portfolioLabelById],
  );

  const {
    practiceViewerAccessList,
    isLoading: isPracticeViewerLoading,
    isError: isPracticeViewerError,
    createMutation: createPracticeViewerMutation,
    updateMutation: updatePracticeViewerMutation,
    deleteMutation: deletePracticeViewerMutation,
  } = usePracticeViewerAccess();

  const [practiceViewerDialogState, setPracticeViewerDialogState] = useState<{
    isOpen: boolean;
    editingItem: IPracticeViewerAccess | null;
  }>({
    isOpen: false,
    editingItem: null,
  });
  const [practiceViewerDeleteTarget, setPracticeViewerDeleteTarget] =
    useState<IPracticeViewerAccess | null>(null);

  const handleOpenPracticeViewerDialog = useCallback(
    (item: IPracticeViewerAccess | null = null) => {
      setPracticeViewerDialogState({ isOpen: true, editingItem: item });
    },
    [],
  );
  const handleClosePracticeViewerDialog = useCallback(() => {
    setPracticeViewerDialogState({ isOpen: false, editingItem: null });
  }, []);

  const handleSavePracticeViewer = useCallback(
    async (values: {
      user: { userId: string; displayName?: string };
      practiceIds: string[];
    }) => {
      if (practiceViewerDialogState.editingItem) {
        await updatePracticeViewerMutation.mutateAsync({
          ...practiceViewerDialogState.editingItem,
          ...values,
        });
      } else {
        await createPracticeViewerMutation.mutateAsync(values);
      }
      handleClosePracticeViewerDialog();
    },
    [
      practiceViewerDialogState.editingItem,
      updatePracticeViewerMutation,
      createPracticeViewerMutation,
      handleClosePracticeViewerDialog,
    ],
  );

  const handleConfirmDeletePracticeViewer = useCallback(async () => {
    if (!practiceViewerDeleteTarget) return;
    await deletePracticeViewerMutation.mutateAsync(
      practiceViewerDeleteTarget.id,
    );
    setPracticeViewerDeleteTarget(null);
  }, [practiceViewerDeleteTarget, deletePracticeViewerMutation]);

  const practiceViewerColumns = useMemo<ITableColumn<IPracticeViewerAccess>[]>(
    () => [
      {
        columnId: "name",
        displayValue: "Name",
        className: colStyles.normal,
        renderHeaderCell: () => "Name",
        compare: (a, b) =>
          (a.user.displayName ?? "").localeCompare(b.user.displayName ?? ""),
        renderCell: (item) => item.user.displayName || item.user.userId,
      },
      {
        columnId: "practices",
        displayValue: "Practices",
        className: colStyles.wide,
        renderHeaderCell: () => "Practices",
        compare: (a, b) =>
          a.practiceIds
            .map((id) => practiceLabelById.get(id) ?? "")
            .join(", ")
            .localeCompare(
              b.practiceIds
                .map((id) => practiceLabelById.get(id) ?? "")
                .join(", "),
            ),
        renderCell: (item) =>
          item.practiceIds
            .map((id) => practiceLabelById.get(id))
            .filter((label): label is string => Boolean(label))
            .join(", ") || "—",
      },
    ],
    [colStyles, practiceLabelById],
  );

  const {
    executiveAccessList,
    isLoading: isExecutiveAccessLoading,
    isError: isExecutiveAccessError,
    createMutation: createExecutiveAccessMutation,
    updateMutation: updateExecutiveAccessMutation,
    deleteMutation: deleteExecutiveAccessMutation,
  } = useExecutiveAccess();

  const [executiveAccessDialogState, setExecutiveAccessDialogState] = useState<{
    isOpen: boolean;
    editingItem: IExecutiveAccess | null;
  }>({
    isOpen: false,
    editingItem: null,
  });
  const [executiveAccessDeleteTarget, setExecutiveAccessDeleteTarget] =
    useState<IExecutiveAccess | null>(null);

  const handleOpenExecutiveAccessDialog = useCallback(
    (item: IExecutiveAccess | null = null) => {
      setExecutiveAccessDialogState({ isOpen: true, editingItem: item });
    },
    [],
  );
  const handleCloseExecutiveAccessDialog = useCallback(() => {
    setExecutiveAccessDialogState({ isOpen: false, editingItem: null });
  }, []);

  const handleSaveExecutiveAccess = useCallback(
    async (values: { user: { userId: string; displayName?: string } }) => {
      if (executiveAccessDialogState.editingItem) {
        await updateExecutiveAccessMutation.mutateAsync({
          ...executiveAccessDialogState.editingItem,
          ...values,
        });
      } else {
        await createExecutiveAccessMutation.mutateAsync(values);
      }
      handleCloseExecutiveAccessDialog();
    },
    [
      executiveAccessDialogState.editingItem,
      updateExecutiveAccessMutation,
      createExecutiveAccessMutation,
      handleCloseExecutiveAccessDialog,
    ],
  );

  const handleConfirmDeleteExecutiveAccess = useCallback(async () => {
    if (!executiveAccessDeleteTarget) return;
    await deleteExecutiveAccessMutation.mutateAsync(
      executiveAccessDeleteTarget.id,
    );
    setExecutiveAccessDeleteTarget(null);
  }, [executiveAccessDeleteTarget, deleteExecutiveAccessMutation]);

  const executiveAccessColumns = useMemo<ITableColumn<IExecutiveAccess>[]>(
    () => [
      {
        columnId: "name",
        displayValue: "Name",
        className: colStyles.normal,
        renderHeaderCell: () => "Name",
        compare: (a, b) =>
          (a.user.displayName ?? "").localeCompare(b.user.displayName ?? ""),
        renderCell: (item) => item.user.displayName || item.user.userId,
      },
    ],
    [colStyles],
  );

  return (
    <div className={styles.root}>
      <div className={styles.tabBar}>
        <TabList
          selectedValue={activeTab}
          onTabSelect={(_ev: SelectTabEvent, data: SelectTabData) =>
            setActiveTab(data.value as PortfoliosPracticesAccessTab)
          }
          aria-label="Portfolio and Practices access sections"
        >
          <Tab id="tab-portfolio-viewer" value="portfolioViewer">
            Portfolio Viewer
          </Tab>
          <Tab id="tab-practice-viewer" value="practiceViewer">
            Practice Viewer
          </Tab>
          <Tab id="tab-executive-access" value="executiveAccess">
            Executive Access
          </Tab>
        </TabList>
      </div>

      {activeTab === "portfolioViewer" && (
        <div
          role="tabpanel"
          aria-labelledby="tab-portfolio-viewer"
          tabIndex={0}
          className={styles.tabContent}
        >
          <AccessListSection<IPortfolioViewerAccess>
            cacheKey="portfolioViewerAccessList"
            items={portfolioViewerAccessList}
            isLoading={isPortfolioViewerLoading}
            isError={isPortfolioViewerError}
            columns={portfolioViewerColumns}
            Dialog={PortfolioViewerAccessDialog}
            dialogProps={{}}
            entityName="portfolio viewer access"
            emptyStateMessage="No portfolio viewer access granted yet."
            createMutation={createPortfolioViewerMutation}
            updateMutation={updatePortfolioViewerMutation}
            deleteMutation={deletePortfolioViewerMutation}
            onDialogDismiss={handleClosePortfolioViewerDialog}
            onDialogSave={handleSavePortfolioViewer}
            onEditRow={handleOpenPortfolioViewerDialog}
            onDeleteRow={setPortfolioViewerDeleteTarget}
            onAddNew={() => handleOpenPortfolioViewerDialog()}
            editingItem={portfolioViewerDialogState.editingItem}
            isDialogOpen={portfolioViewerDialogState.isOpen}
            deleteTarget={portfolioViewerDeleteTarget}
            onConfirmDelete={handleConfirmDeletePortfolioViewer}
            onClearDeleteTarget={() => setPortfolioViewerDeleteTarget(null)}
          />
        </div>
      )}

      {activeTab === "practiceViewer" && (
        <div
          role="tabpanel"
          aria-labelledby="tab-practice-viewer"
          tabIndex={0}
          className={styles.tabContent}
        >
          <AccessListSection<IPracticeViewerAccess>
            cacheKey="practiceViewerAccessList"
            items={practiceViewerAccessList}
            isLoading={isPracticeViewerLoading}
            isError={isPracticeViewerError}
            columns={practiceViewerColumns}
            Dialog={PracticeViewerAccessDialog}
            dialogProps={{}}
            entityName="practice viewer access"
            emptyStateMessage="No practice viewer access granted yet."
            createMutation={createPracticeViewerMutation}
            updateMutation={updatePracticeViewerMutation}
            deleteMutation={deletePracticeViewerMutation}
            onDialogDismiss={handleClosePracticeViewerDialog}
            onDialogSave={handleSavePracticeViewer}
            onEditRow={handleOpenPracticeViewerDialog}
            onDeleteRow={setPracticeViewerDeleteTarget}
            onAddNew={() => handleOpenPracticeViewerDialog()}
            editingItem={practiceViewerDialogState.editingItem}
            isDialogOpen={practiceViewerDialogState.isOpen}
            deleteTarget={practiceViewerDeleteTarget}
            onConfirmDelete={handleConfirmDeletePracticeViewer}
            onClearDeleteTarget={() => setPracticeViewerDeleteTarget(null)}
          />
        </div>
      )}

      {activeTab === "executiveAccess" && (
        <div
          role="tabpanel"
          aria-labelledby="tab-executive-access"
          tabIndex={0}
          className={styles.tabContent}
        >
          <AccessListSection<IExecutiveAccess>
            cacheKey="executiveAccessList"
            items={executiveAccessList}
            isLoading={isExecutiveAccessLoading}
            isError={isExecutiveAccessError}
            columns={executiveAccessColumns}
            Dialog={ExecutiveAccessDialog}
            dialogProps={{}}
            entityName="executive access"
            emptyStateMessage="No executive access granted yet."
            createMutation={createExecutiveAccessMutation}
            updateMutation={updateExecutiveAccessMutation}
            deleteMutation={deleteExecutiveAccessMutation}
            onDialogDismiss={handleCloseExecutiveAccessDialog}
            onDialogSave={handleSaveExecutiveAccess}
            onEditRow={handleOpenExecutiveAccessDialog}
            onDeleteRow={setExecutiveAccessDeleteTarget}
            onAddNew={() => handleOpenExecutiveAccessDialog()}
            editingItem={executiveAccessDialogState.editingItem}
            isDialogOpen={executiveAccessDialogState.isOpen}
            deleteTarget={executiveAccessDeleteTarget}
            onConfirmDelete={handleConfirmDeleteExecutiveAccess}
            onClearDeleteTarget={() => setExecutiveAccessDeleteTarget(null)}
          />
        </div>
      )}
    </div>
  );
};
